import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { supabase } from '../supabase';
import {
  Mail,
  Lock,
  User,
  Store,
  Phone,
  ArrowRight,
  Loader2,
  CheckCircle2,
  ArrowLeft,
  AlertCircle,
  Sparkles,
  ShieldCheck,
  MailCheck,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { trackOnboardingEvent } from '../utils/onboardingTelemetry';
import { trackAccountCreated } from '../utils/metaPixel';
import '../components/public/publicSite.css';
import { traducirErrorAuth } from '../utils/authErrors';
import { usePublicPlans } from '../hooks/usePublicPlans';

/* ── Gastify "G" mark ── */
function GastifyMark({ size = 44 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <linearGradient id="gst-reg-bg" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#10B981" />
          <stop offset="100%" stopColor="#059669" />
        </linearGradient>
      </defs>
      <rect width="100" height="100" rx="22" fill="url(#gst-reg-bg)" />
      <circle cx="50" cy="50" r="24" fill="none" stroke="white" strokeWidth="9"
        strokeLinecap="round" strokeDasharray="108 43"
        transform="rotate(-55 50 50)" />
      <rect x="50" y="46" width="20" height="8" rx="4" fill="white" />
    </svg>
  );
}

/* ── Validaciones livianas ── */
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;
const validateEmail = (email) => {
  if (!EMAIL_REGEX.test(email)) return 'Ingresá un email válido (ej: nombre@gmail.com)';
  const domain = email.split('@')[1]?.toLowerCase() ?? '';
  const [sld] = domain.split('.');
  if (sld.length <= 1) return 'Dominio no válido. Usá un email real.';
  return null;
};

// Normalización mínima de teléfono a algo cercano a E.164 (sin libphonenumber
// para no agregar dependencia). Si querés validación dura, agregá libphonenumber-js
// y cambiá esta función por phoneNumber.isValid() / formatInternational().
const TURNSTILE_SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || '';
const normalizePhone = (raw = '') => {
  const cleaned = String(raw).replace(/[^\d+]/g, '');
  if (!cleaned) return '';
  if (cleaned.startsWith('+')) return cleaned;
  
  // Argentina default mobile formatting: +54 9 ...
  let withArea = cleaned.replace(/^0+/, '').replace(/^15/, '9');
  
  // If it has 10 digits (e.g. 1134567890) and doesn't start with 9, it's Argentine mobile without '9':
  if (withArea.length === 10 && !withArea.startsWith('9')) {
    withArea = '9' + withArea;
  }
  
  return `+54${withArea}`;
};
// Validación blanda: si el usuario completó algo, debe tener al menos 8 dígitos
// (para no aceptar basura). Si dejó vacío, está OK — el teléfono es opcional.
const isAcceptablePhone = (raw = '') => {
  const trimmed = String(raw).trim();
  if (!trimmed) return true; // opcional
  const cleaned = trimmed.replace(/[^\d+]/g, '');
  return cleaned.replace(/\+/g, '').length >= 8;
};

// H11 — Feedback simple de contraseña (largo + variedad). No bloquea nada por
// encima del mínimo de 6 que exige Supabase; solo orienta.
const getPasswordStrength = (pwd = '') => {
  if (!pwd) return null;
  if (pwd.length < 6) {
    return { level: 0, label: `Faltan ${6 - pwd.length} caracteres`, color: '#f87171' };
  }
  let score = 1;
  if (pwd.length >= 10) score += 1;
  if (/[a-zA-Z]/.test(pwd) && /\d/.test(pwd)) score += 1;
  if (/[^a-zA-Z0-9]/.test(pwd)) score += 1;
  if (score <= 2) return { level: 1, label: 'Débil', color: '#f59e0b' };
  if (score === 3) return { level: 2, label: 'Buena', color: '#10b981' };
  return { level: 3, label: 'Muy buena', color: '#059669' };
};

// El mapeo vive en utils/authErrors.js y es el mismo para registro, login y
// reset. Antes cada pantalla tenia el suyo, y el de aca buscaba la palabra
// "short" — que Supabase no usa: manda "Password should be at least 6
// characters". No matcheaba, caia al `return msg` y el error de contraseña
// corta salia en ingles en medio de una UI en español.
const getAuthErrorMsg = (error) => traducirErrorAuth(error);

/**
 * RegistroOnboarding · Paso 1 de 2
 * --------------------------------------
 * Pide solo lo mínimo para abrir cuenta: nombre, negocio, email, WhatsApp, password.
 * NO se elige plan ni se muestra precio acá: eso es el Paso 2.
 *
 * Principios UX aplicados:
 *  · "Foot-in-the-door": un solo pedido pequeño primero.
 *  · "Hick's Law": una decisión por pantalla.
 *  · "Reversibilidad declarada": chips de confianza (sin tarjeta / cancelás cuando quieras).
 *
 * Al hacer signUp OK, guarda los datos del negocio en sessionStorage
 * y redirige a /registro-onboarding/plan donde se elige plan y se hace
 * el provisioning de la organización.
 */
export default function RegistroOnboarding() {
  // Mismo origen que la landing y precios: app_settings.default_trial_days.
  const { trialDays } = usePublicPlans();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    nombreUsuario: '',
    email: '',
    password: '',
    nombreNegocio: '',
    telefono: '',
  });

  const [emailError, setEmailError] = useState(null);
  const [errorMsg, setErrorMsg] = useState(null);
  // Si Supabase Auth tiene email-confirmation activado, el signUp no
  // devuelve session. En vez de romper el flujo mostramos esta vista
  // y, cuando el usuario confirma desde el mail, el listener nos manda
  // automáticamente al paso 2.
  const [pendingConfirmation, setPendingConfirmation] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  // 'pending' = cargando, 'ready' = widget montado, 'unavailable' = no cargó en 8s
  const [turnstileState, setTurnstileState] = useState(TURNSTILE_SITE_KEY ? 'pending' : 'ready');

  // Cargar el script de Cloudflare Turnstile si está configurado.
  // Si no hay site key, se omite y el form anda igual (sin captcha).
  // Si carga falla (AdBlock, red bloqueada, etc.), después de 8s permitimos
  // continuar igual con un warning, para no bloquear conversiones por captcha caído.
  useEffect(() => {
    if (!TURNSTILE_SITE_KEY) return undefined;
    if (document.querySelector('script[data-turnstile]')) {
      // Script ya cargado en otra entrada al form
      setTurnstileState('ready');
      return undefined;
    }
    const script = document.createElement('script');
    script.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js';
    script.async = true;
    script.defer = true;
    script.setAttribute('data-turnstile', '1');
    script.onload = () => setTurnstileState('ready');
    script.onerror = () => setTurnstileState('unavailable');
    document.head.appendChild(script);
    window.onTurnstileSuccess = (token) => setTurnstileToken(token || '');

    // Fallback de tiempo: si después de 8s el widget no inicializó (ej. AdBlock que
    // deja cargar el JS pero bloquea el iframe), liberamos el bloqueo.
    const failsafe = setTimeout(() => {
      setTurnstileState((current) => (current === 'pending' ? 'unavailable' : current));
    }, 8000);

    return () => {
      clearTimeout(failsafe);
      window.onTurnstileSuccess = undefined;
    };
  }, []);

  // Listener para retomar paso 2 cuando confirme el email en otra pestaña.
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (session?.user && (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED')) {
        navigate('/registro-onboarding/plan', { replace: true });
      }
    });
    return () => subscription.unsubscribe();
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((current) => ({ ...current, [name]: value }));
    if (name === 'email') setEmailError(null);
  };

  const handleRegisterSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;

    const emailErr = validateEmail(formData.email);
    if (emailErr) {
      setEmailError(emailErr);
      return;
    }

    if (!isAcceptablePhone(formData.telefono)) {
      setErrorMsg('El WhatsApp tiene pocos dígitos. Dejalo vacío o ingresá uno válido.');
      return;
    }

    // Captcha:
    //   - 'ready' + sin token → pedile que lo complete
    //   - 'unavailable' → permitimos continuar sin captcha (no bloquear conversiones por AdBlock).
    //     El backend de Supabase Auth igualmente puede rechazar si tiene captcha obligatorio.
    if (TURNSTILE_SITE_KEY && turnstileState === 'ready' && !turnstileToken) {
      setErrorMsg('Resolvé el captcha (la casilla "No soy un robot") antes de continuar.');
      return;
    }

    setSubmitting(true);
    setErrorMsg(null);

    try {
      const normalizedPhone = normalizePhone(formData.telefono);
      const { data: authData, error: authError } = await supabase.auth.signUp({
        email: formData.email.trim().toLowerCase(),
        password: formData.password,
        options: {
          data: {
            full_name: formData.nombreUsuario,
            role: 'admin',
            phone: normalizedPhone,
            business_name: formData.nombreNegocio,
            captcha_token: turnstileToken || undefined,
          },
          emailRedirectTo: `${window.location.origin}/registro-onboarding/plan`,
          captchaToken: turnstileToken || undefined,
        },
      });

      if (authError) throw new Error(getAuthErrorMsg(authError));
      if (!authData?.user) throw new Error('No se pudo crear el usuario.');

      // H1 — hito del embudo: usuario creado en auth, todavía sin organización.
      // Antes el primer evento era 'signup' recién al crear la org en el paso 2,
      // dejando ciego el tramo con más abandono. Best-effort: si no hay sesión
      // (confirmación de email pendiente) el insert corre como anon y la RLS
      // puede rechazarlo; el evento de respaldo es 'plan_step_viewed' al llegar
      // al paso 2 ya autenticado.
      trackOnboardingEvent({
        event: 'auth_signup',
        userId: authData.user.id,
        metadata: { emailConfirmationPending: !authData?.session },
      });

      // Píxel: cuenta creada (paso 1). Le da a Meta una señal más frecuente
      // que CompleteRegistration (paso 2) mientras la campaña aprende.
      trackAccountCreated();

      // Guardamos lo del negocio (lo necesita el paso 2 — esté o no la sesión)
      const datosNegocioPayload = JSON.stringify({
        nombreUsuario: formData.nombreUsuario,
        nombreNegocio: formData.nombreNegocio,
        telefono: normalizedPhone,
        email: formData.email.trim().toLowerCase(),
      });
      try {
        window.sessionStorage.setItem('gastify:registro-onboarding:datos-negocio', datosNegocioPayload);
        window.localStorage.setItem('gastify:registro-onboarding:datos-negocio', datosNegocioPayload);
      } catch { /* incógnito puede fallar, no es crítico */ }

      if (!authData?.session) {
        // Email confirmation activado en Supabase: el usuario tiene que
        // hacer click en el mail. Mostramos UI explicativa y dejamos
        // el listener (useEffect arriba) llevarlo al paso 2 cuando confirme.
        trackOnboardingEvent({
          event: 'email_confirmation_pending',
          userId: authData.user.id,
        });
        setPendingConfirmation(true);
        toast.success('Te mandamos un mail para confirmar la cuenta.');
        setSubmitting(false);
        return;
      }

      toast.success('¡Cuenta creada! Elegí con qué plan querés arrancar.');
      navigate(`/registro-onboarding/plan${location.search}`, { replace: true });
    } catch (err) {
      console.error('[RegistroOnboarding] signUp error:', err);
      setErrorMsg(err.message);
      toast.error(err.message, { duration: 6000 });
      setSubmitting(false);
    }
  };

  const handleResendConfirmation = async () => {
    if (!formData.email) return;
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: formData.email.trim().toLowerCase(),
      });
      if (error) throw error;
      toast.success('Mail reenviado. Revisá tu bandeja (y spam).');
    } catch (err) {
      toast.error(err?.message || 'No pudimos reenviar el mail.');
    }
  };

  const inputCls = (hasError = false) =>
    `w-full bg-[var(--surface)] border ${hasError ? 'border-red-400' : 'border-[var(--gst-border-strong)]'} text-[var(--gst-graphite-900)] rounded-xl pl-10 pr-4 py-2.5 text-[13px] font-semibold outline-none focus:border-[var(--gst-mint-400)] focus:ring-[3px] focus:ring-[var(--gst-mint-100)] transition-all placeholder:text-[var(--gst-graphite-300)] shadow-sm`;

  return (
    <div className="gst-site-tokens h-screen w-screen overflow-hidden bg-[var(--gst-cream-50)] text-[var(--gst-graphite-900)] flex flex-col md:p-3" style={{ fontFamily: "'Manrope', system-ui, sans-serif" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,600;9..144,700;9..144,800&family=Manrope:wght@400;500;600;700;800&display=swap');
        .gst-public-display { font-family: 'Fraunces', Georgia, serif; }
      `}</style>

      {/* Back button */}
      <div className="absolute top-5 left-5 z-20">
        <button
          onClick={() => navigate('/')}
          className="flex items-center justify-center gap-2 rounded-full border border-[var(--gst-border-strong)] bg-[var(--surface)] px-4 py-2 text-xs font-bold uppercase tracking-[0.1em] text-[var(--gst-graphite-700)] shadow-sm transition hover:border-[var(--gst-mint-300)] hover:text-[var(--gst-mint-700)]"
        >
          <ArrowLeft size={14} /> Volver
        </button>
      </div>

      <div className="flex flex-1 overflow-hidden rounded-[2rem] bg-[var(--surface)] border border-[var(--gst-border)] shadow-xl relative">

        {/* Left side: hero */}
        <div className="hidden lg:flex flex-col justify-center relative w-5/12 bg-[linear-gradient(180deg,var(--gst-cream-100),var(--gst-surface))] border-r border-[var(--gst-border)] p-12 overflow-hidden">
          <div className="absolute -left-6 top-8 h-32 w-32 rounded-full bg-[var(--gst-peach)]/50 blur-3xl pointer-events-none" />
          <div className="absolute -right-6 bottom-10 h-40 w-40 rounded-full bg-[var(--gst-mint-200)]/60 blur-3xl pointer-events-none" />

          <div className="relative z-10">
            <div className="inline-flex items-center gap-2 rounded-full bg-[var(--gst-mint-100)] px-3 py-1.5 text-[10px] font-black uppercase tracking-[0.2em] text-[var(--gst-mint-700)] mb-6">
              <Sparkles size={14} />
              Paso 1 de 2
            </div>

            <GastifyMark size={50} />

            <h1 className="gst-public-display text-4xl xl:text-5xl font-black leading-[1.05] tracking-[-0.03em] mt-6 mb-4 text-[var(--gst-graphite-900)]">
              Registrate gratis<br />en 30 segundos.
            </h1>

            <p className="text-[15px] leading-relaxed text-[var(--gst-graphite-700)] max-w-sm mb-10">
              Sin tarjeta. {trialDays} días para probar Gastify completo. Decidís el plan adentro, no acá.
            </p>

            <div className="space-y-4">
              {[
                { icon: ShieldCheck, title: 'Sin tarjeta hoy', desc: 'No te pedimos datos de pago para empezar.' },
                { icon: CheckCircle2, title: 'Cancelás cuando quieras', desc: 'Si no te sirve, simplemente no activás el plan.' },
                { icon: Sparkles, title: 'Los días gratis no se pierden', desc: 'Si pagás antes, sumamos los días restantes al primer mes.' },
              ].map((item, idx) => (
                <div key={idx} className="flex items-start gap-3 rounded-2xl border border-[var(--gst-border-strong)] bg-[var(--surface)]/60 p-4 backdrop-blur-sm shadow-sm max-w-md">
                  <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-xl bg-[var(--gst-mint-100)] text-[var(--gst-mint-600)]">
                    <item.icon size={16} />
                  </div>
                  <div>
                    <h4 className="text-xs font-black text-[var(--gst-graphite-900)]">{item.title}</h4>
                    <p className="text-[11px] font-medium text-[var(--gst-graphite-600)]">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Right side: form */}
        <div className="flex-1 flex flex-col justify-center items-center p-6 lg:p-12 relative overflow-y-auto">
          <div className="w-full max-w-md">
            <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">

              <div className="mb-6 lg:hidden flex justify-center">
                <GastifyMark size={40} />
              </div>

              <div className="mb-6 text-center lg:text-left">
                <h2 className="gst-public-display text-3xl font-black text-[var(--gst-graphite-900)] mb-1.5">
                  Creá tu cuenta gratis
                </h2>
                <p className="text-[13px] font-medium text-[var(--gst-graphite-600)]">
                  Sin tarjeta. El plan lo elegís en el próximo paso.
                </p>
              </div>

              {errorMsg && (
                <div className="mb-5 flex items-start gap-2 rounded-xl bg-red-50 border border-red-100 p-3 text-red-600 text-[13px] font-semibold">
                  <AlertCircle size={16} className="shrink-0 mt-0.5" />
                  <span>{errorMsg}</span>
                </div>
              )}

              {pendingConfirmation && (
                <div className="mb-5 rounded-2xl border border-[var(--gst-mint-200)] bg-[var(--gst-mint-50,#ECFDF5)] p-5 text-center">
                  <div className="mx-auto mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full bg-[var(--gst-mint-100)]">
                    <MailCheck size={22} className="text-[var(--gst-mint-700)]" />
                  </div>
                  <h3 className="text-[15px] font-black text-[var(--gst-graphite-900)] mb-1">
                    Confirmá tu correo
                  </h3>
                  <p className="text-[12px] text-[var(--gst-graphite-700)] leading-relaxed mb-4">
                    Te mandamos un mail a <strong>{formData.email}</strong>.
                    Hacé click en el link de confirmación para activar la cuenta y elegir tu plan.
                  </p>
                  <div className="flex flex-col gap-2">
                    <button
                      type="button"
                      onClick={handleResendConfirmation}
                      className="w-full inline-flex items-center justify-center gap-2 rounded-xl border border-[var(--gst-mint-300)] bg-[var(--surface)] px-4 py-2.5 text-[11px] font-black uppercase tracking-widest text-[var(--gst-mint-700)] hover:bg-[var(--gst-mint-100)] transition"
                    >
                      Reenviar mail de confirmación
                    </button>
                    <button
                      type="button"
                      onClick={() => setPendingConfirmation(false)}
                      className="text-[11px] font-bold text-[var(--gst-graphite-500)] hover:text-[var(--gst-graphite-900)] underline"
                    >
                      Usar otro email
                    </button>
                  </div>
                </div>
              )}

              <form onSubmit={handleRegisterSubmit} className="grid grid-cols-2 gap-x-3 gap-y-4">
                {/* Nombre */}
                <div className="col-span-2 sm:col-span-1">
                  <label className="block text-[10px] font-black uppercase tracking-[0.15em] text-[var(--gst-graphite-700)] mb-1.5 ml-1">
                    Tu nombre
                  </label>
                  <div className="relative">
                    <User size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--gst-graphite-400)] pointer-events-none" />
                    <input
                      type="text"
                      name="nombreUsuario"
                      autoComplete="name"
                      value={formData.nombreUsuario}
                      onChange={handleChange}
                      required
                      className={inputCls()}
                      placeholder="Ej: Marcos Pérez"
                    />
                  </div>
                </div>

                {/* Negocio */}
                <div className="col-span-2 sm:col-span-1">
                  <label className="block text-[10px] font-black uppercase tracking-[0.15em] text-[var(--gst-graphite-700)] mb-1.5 ml-1">
                    Negocio
                  </label>
                  <div className="relative">
                    <Store size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--gst-graphite-400)] pointer-events-none" />
                    <input
                      type="text"
                      name="nombreNegocio"
                      autoComplete="organization"
                      value={formData.nombreNegocio}
                      onChange={handleChange}
                      required
                      className={inputCls()}
                      placeholder="Ej: Minimarket El Sol"
                    />
                  </div>
                </div>

                {/* Email */}
                <div className="col-span-2">
                  <label className="block text-[10px] font-black uppercase tracking-[0.15em] text-[var(--gst-graphite-700)] mb-1.5 ml-1">
                    Correo Electrónico
                  </label>
                  <div className="relative">
                    <Mail size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--gst-graphite-400)] pointer-events-none" />
                    <input
                      type="email"
                      name="email"
                      autoComplete="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className={inputCls(!!emailError)}
                      placeholder="tu@correo.com"
                    />
                  </div>
                  {emailError && (
                    <p className="text-red-500 text-[11px] font-bold mt-1.5 ml-1">{emailError}</p>
                  )}
                </div>

                {/* WhatsApp (opcional) */}
                <div className="col-span-2 sm:col-span-1">
                  <label className="flex items-center justify-between text-[10px] font-black uppercase tracking-[0.15em] text-[var(--gst-graphite-700)] mb-1.5 ml-1">
                    <span>WhatsApp</span>
                    <span className="text-[9px] font-bold normal-case tracking-normal text-[var(--gst-graphite-400)]">opcional</span>
                  </label>
                  <div className="relative">
                    <Phone size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--gst-graphite-400)] pointer-events-none" />
                    <input
                      type="tel"
                      name="telefono"
                      autoComplete="tel"
                      value={formData.telefono}
                      onChange={handleChange}
                      className={inputCls()}
                      placeholder="+54 9 11 1234 5678"
                    />
                  </div>
                </div>

                {/* Contraseña */}
                <div className="col-span-2 sm:col-span-1">
                  <label className="block text-[10px] font-black uppercase tracking-[0.15em] text-[var(--gst-graphite-700)] mb-1.5 ml-1">
                    Contraseña
                  </label>
                  <div className="relative">
                    <Lock size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--gst-graphite-400)] pointer-events-none" />
                    <input
                      type="password"
                      name="password"
                      autoComplete="new-password"
                      value={formData.password}
                      onChange={handleChange}
                      required
                      minLength={6}
                      className={inputCls()}
                      placeholder="Mínimo 6 caracteres"
                    />
                  </div>
                  {(() => {
                    const strength = getPasswordStrength(formData.password);
                    if (!strength) return null;
                    return (
                      <div className="mt-1.5 ml-1 flex items-center gap-2">
                        <div className="flex gap-1">
                          {[1, 2, 3].map((i) => (
                            <span
                              key={i}
                              className="h-1 w-6 rounded-full transition-colors"
                              style={{ background: strength.level >= i ? strength.color : 'var(--gst-border, #e5e7eb)' }}
                            />
                          ))}
                        </div>
                        <span className="text-[10px] font-bold" style={{ color: strength.color }}>
                          {strength.label}
                        </span>
                      </div>
                    );
                  })()}
                </div>

                {/* Captcha (solo si está configurado VITE_TURNSTILE_SITE_KEY) */}
                {TURNSTILE_SITE_KEY && turnstileState !== 'unavailable' && (
                  <div className="col-span-2 flex justify-center">
                    <div
                      className="cf-turnstile"
                      data-sitekey={TURNSTILE_SITE_KEY}
                      data-callback="onTurnstileSuccess"
                      data-theme="light"
                    />
                  </div>
                )}
                {TURNSTILE_SITE_KEY && turnstileState === 'unavailable' && (
                  <div className="col-span-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] font-medium text-amber-700 text-center">
                    No pudimos cargar el captcha (probablemente un AdBlock o tu red lo bloqueó).
                    Te dejamos continuar igual.
                  </div>
                )}

                {/* CTA */}
                <div className="col-span-2 mt-2">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[var(--gst-mint-500)] px-6 py-3.5 text-sm font-black uppercase tracking-[0.12em] text-[var(--text-default)] shadow-[0_12px_24px_rgba(16,185,129,0.2)] transition hover:bg-[var(--gst-mint-600)] hover:shadow-[0_16px_32px_rgba(16,185,129,0.3)] disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {submitting ? (
                      <>
                        <Loader2 size={16} className="animate-spin" /> Creando cuenta...
                      </>
                    ) : (
                      <>
                        Registrarse gratis <ArrowRight size={16} />
                      </>
                    )}
                  </button>
                </div>

                {/* Trust chips */}
                <div className="col-span-2 flex flex-wrap items-center justify-center gap-2 mt-1">
                  <span className="inline-flex items-center gap-1 rounded-full bg-[var(--gst-cream-50)] border border-[var(--gst-border)] px-3 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-[var(--gst-graphite-700)]">
                    ✓ Sin tarjeta
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-full bg-[var(--gst-cream-50)] border border-[var(--gst-border)] px-3 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-[var(--gst-graphite-700)]">
                    ✓ Cancelás cuando quieras
                  </span>
                  <span className="inline-flex items-center gap-1 rounded-full bg-[var(--gst-cream-50)] border border-[var(--gst-border)] px-3 py-1 text-[10px] font-black uppercase tracking-[0.12em] text-[var(--gst-graphite-700)]">
                    ✓ {trialDays} días completos
                  </span>
                </div>
              </form>

              <p className="mt-6 text-center text-xs text-[var(--gst-graphite-500)] font-medium">
                ¿Ya tenés cuenta?{' '}
                <Link to="/login" className="text-[var(--gst-mint-700)] font-bold hover:underline">
                  Iniciá sesión
                </Link>
              </p>

              <p className="mt-4 text-center text-[10px] text-[var(--gst-graphite-400)] font-medium">
                Al registrarte, aceptás nuestros{' '}
                <Link to="/legales/terminos" className="underline hover:text-[var(--gst-graphite-700)] transition-colors">Términos</Link>{' '}
                y{' '}
                <Link to="/legales/privacidad" className="underline hover:text-[var(--gst-graphite-700)] transition-colors">Privacidad</Link>.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
